import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import { SelectValue } from 'antd/lib/select';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { searchRegions } from '../../../service/VolunteerApplicationService';
import { displayError } from '../../../shared/Utils';
import { useToastMessage } from '../../../hooks/useToastMessage';

const { Option } = Select;

interface OptionData {
	value: string | undefined;
	label: string | undefined;
}

interface RemoteSingleSelectProps {
	onChange?: (value: OptionData) => void;
	value?: OptionData | null | undefined;
	functionarySrcmId?: string;
	isFetchedDataBySRCMID?: boolean;
}

const RemoteSingleSelect: React.FC<RemoteSingleSelectProps> = ({
	onChange,
	value,
	functionarySrcmId,
	isFetchedDataBySRCMID,
}: RemoteSingleSelectProps) => {
	const { t } = useTranslation();
	const { showToastMessage } = useToastMessage();
	const [options, setOptions] = useState<OptionData[]>([]);
	const [loading, setLoading] = useState(false);
	const [selectedValue, setSelectedValue] = useState<OptionData | undefined>({
		value: value?.value,
		label: value?.label,
	});

	const fetchData = useCallback(
		async (searchValue: string) => {
			setLoading(true);
			if (functionarySrcmId) {
				try {
					const regionsPage = await searchRegions({
						functionarySrcmId,
						pageIndex: 0,
						pageSize: 100,
						searchKey: searchValue,
						includeOnlyRegionsOfApprover: true,
						displayAllRegions: true,
						isPaginationRequired: searchValue ? true : false,
					});
					if (regionsPage.regions) {
						const transformedOptions: OptionData[] = regionsPage.regions.map(
							(item: { regionId: string; regionName: string }) => ({
								value: item.regionId,
								label: item.regionName,
							})
						);

						setOptions(transformedOptions);
					}
				} catch (err) {
					const errorMsg = displayError(err);
					showToastMessage('error', errorMsg);
				}
			} else {
				setOptions([]);
			}

			setLoading(false);
		},
		[functionarySrcmId, showToastMessage]
	);

	useEffect(() => {
		if (value?.value) {
			setSelectedValue(value);
			void fetchData('');
		} else {
			setSelectedValue(undefined);
		}
	}, [fetchData, value]);

	const debouncedFetchData = useMemo(
		() => debounce(fetchData, 500),
		[fetchData]
	);

	const handleSearch = useCallback(
		(searchKey: SelectValue) => {
			void debouncedFetchData(searchKey as string);
		},
		[debouncedFetchData]
	);

	return (
		<Select
			showSearch
			defaultActiveFirstOption={false}
			filterOption={false}
			placeholder={t('searchRegions')}
			notFoundContent={loading ? <Spin size="small" /> : null}
			onSearch={handleSearch}
			onChange={onChange}
			labelInValue
			value={selectedValue}
			suffixIcon={null}
			className="font-sans text-sm text-secondary-gray"
			size="large"
			disabled={isFetchedDataBySRCMID}
		>
			{options.map((option) => (
				<Option key={option.value} value={option.value}>
					{option.label}
				</Option>
			))}
		</Select>
	);
};

export default RemoteSingleSelect;
